/*
 OK FBI Investigation Game
 Designed & Developed by AMW
 CREATED BY // AMW
*/
import React, { useState, useEffect, useCallback } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Home, Folder, Users, Fingerprint, FlaskConical, Clock, StickyNote, Trophy, Settings, Radio, Eye, EyeOff, Zap, ChevronLeft, ChevronRight, Volume2, VolumeX, Undo2, Info, PenSquare, ShieldCheck, CreditCard, BookOpen, FileText, Bell, Star, Shield, Lock, Key, Globe, Compass, Map as MapIcon, Newspaper, Megaphone, MessageSquare, Video, Image as ImageIcon, Award, Sparkles, Bookmark, Link as LinkIcon, User } from "lucide-react";
import { MembershipBadge } from "@/pages/Phase2Pages";
import { useSession, assetUrl } from "@/contexts/SessionContext";
import { api } from "@/lib/api";
import { HostLogoutButton, useAuth } from "@/pages/AuthPage";
import { sfx, setSoundEnabled, isSoundEnabled, setAmbienceEnabled, isAmbienceEnabled, setAmbienceVolume, getAmbienceVolume, playAmbienceFor, stopAmbienceBed } from "@/lib/sounds";
// useOverlayHotkeys removed — no application-wide keyboard shortcuts.
import { CueCardsDock } from "@/components/CueCardsDock";
import { Music2 } from "lucide-react";
import { CaseTimer } from "@/components/ForensicLab";
import { StreamPlatformsModal } from "@/components/streamer/StreamPlatformsModal";
import { toast } from "sonner";

// Icon library for custom-nav items — must match /app/backend/custom_nav.py
// NAV_ICON_LIBRARY. If the OWNER picks an icon not in this map (shouldn't
// happen because the picker is server-driven) we fall back to BookOpen.
const CUSTOM_ICONS = {
  BookOpen, FileText, Bell, Star, Trophy, Shield, Lock, Key, Globe,
  Compass, Map: MapIcon, Newspaper, Megaphone, Users, MessageSquare,
  Video, Image: ImageIcon, Award, Info, Sparkles, Radio, Zap, Bookmark,
  Folder, Link: LinkIcon, User,
};

const NAV = [
  { to: "/app", label: "الرئيسية", icon: Home, perm: null },
  { to: "/profile", label: "الملف الشخصي", icon: User, perm: null },
  { to: "/cases", label: "القضايا", icon: Folder, perm: null },
  // Case-specific modules removed from the GLOBAL sidebar — they live INSIDE
  // each case (via the case detail tabs). OWNER retains sidebar access.
  { to: "/suspects", label: "المشتبه بهم", icon: Users, perm: "VIEW_SUSPECTS", caseTab: true },
  { to: "/evidence", label: "الأدلة", icon: Fingerprint, perm: "VIEW_EVIDENCE", caseTab: true },
  { to: "/forensics", label: "التحليل الجنائي", icon: FlaskConical, perm: "USE_FORENSIC_LAB", caseTab: true },
  { to: "/timeline", label: "الخط الزمني", icon: Clock, perm: null, caseTab: true },
  { to: "/leaderboard", label: "المتصدرون", icon: Trophy, perm: null },
  { to: "/squad", label: "فرقة التحقيق", icon: Users, perm: null },
  { to: "/plans", label: "الباقات", icon: CreditCard, perm: null },
  { to: "/host/case-builder", label: "بناء القضايا", icon: PenSquare, perm: "CREATE_CASE", streamerHidden: true },
  { to: "/ops", label: "غرفة العمليات", icon: Radio, perm: "VIEW_OPERATIONS_ROOM", streamerHidden: true },
  // === OWNER — single consolidated entry ===
  { to: "/host", label: "🔒 مركز المالك", icon: ShieldCheck, perm: null, ownerOnly: true },
  { to: "/host/streamer", label: "لوحة تشغيل البث", icon: Radio, perm: null, streamerOrOwner: true },
  { to: "/host/platforms", label: "منصات البث", icon: Video, perm: null, streamerOrOwner: true, isGroup: true, children: [
    { to: "/host/platforms/tiktok", label: "TikTok", platform: "tiktok" },
    { to: "/host/platforms/twitch", label: "Twitch", platform: "twitch" },
    { to: "/host/platforms/kick", label: "Kick", platform: "kick" },
  ]},
  { to: "/credits", label: "حول اللعبة", icon: Info, perm: null },
  { to: "/settings", label: "الإعدادات", icon: Settings, perm: null },
];

/* ── Platform SVG icons for sidebar sub-items ── */
const PLATFORM_ICONS = {
  tiktok: { color: "text-cyan-400", bg: "bg-black/40", svg: <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 fill-current"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298-.002.595.042.88.13V9.4a6.33 6.33 0 0 0-1-.08A6.34 6.34 0 0 0 3 15.66a6.34 6.34 0 0 0 10.82 4.46V11.8a8.28 8.28 0 0 0 4.77 1.5V9.86a4.87 4.87 0 0 1-1-.17v-3z"/></svg> },
  twitch: { color: "text-[#C084FC]", bg: "bg-[#9146FF]/20", svg: <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 fill-current"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/></svg> },
  kick: { color: "text-[#53FC18]", bg: "bg-[#53FC18]/15", svg: <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 fill-current"><path d="M1.333 0h5.334v8h2.667L14.667 0h6.666l-6.666 9.333 7.333 10.667h-6.667l-6-8.667v8.667H1.333z"/></svg> },
};

/* ── Expandable nav group (used for "منصات البث" sub-items) ── */
function NavGroup({ to, label, Icon, items }) {
  const location = useLocation();
  const isChildActive = items.some(c => location.pathname === c.to || location.pathname.startsWith(c.to + "/"));
  const isParentActive = location.pathname === to;
  const isAnyActive = isChildActive || isParentActive;
  const [open, setOpen] = React.useState(isAnyActive);

  // Auto-expand when a child route becomes active
  React.useEffect(() => { if (isAnyActive) setOpen(true); }, [isAnyActive]);

  return (
    <div data-testid={`nav-group-${to.slice(1)}`}>
      {/* Group header — click navigates to index, chevron toggles expand */}
      <div className="flex items-center">
        <NavLink
          to={to} end
          data-testid={`nav-${to.slice(1)}`}
          className={({ isActive }) => `flex-1 flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-r transition-colors ${isActive ? "bg-[rgba(197,160,89,0.12)] text-gold border-r-2 border-gold" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}
        >
          <Icon size={16} strokeWidth={1.7} className="shrink-0" />
          <span className="flex-1 truncate">{label}</span>
          {/* Platform mini icons */}
          <span className="inline-flex items-center gap-1 shrink-0 select-none mr-1">
            {items.map(c => {
              const pi = PLATFORM_ICONS[c.platform];
              return pi ? (
                <span key={c.platform} className={`w-3.5 h-3.5 rounded flex items-center justify-center ${pi.bg} ${pi.color}`} title={c.label}>
                  {pi.svg}
                </span>
              ) : null;
            })}
          </span>
        </NavLink>
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
          className="px-1.5 py-2 text-zinc-500 hover:text-zinc-300 transition-colors"
          aria-label={open ? "طي القائمة" : "توسيع القائمة"}
        >
          <ChevronRight size={13} className={`transition-transform duration-200 ${open ? "rotate-90" : ""}`} />
        </button>
      </div>

      {/* Sub-items */}
      {open && (
        <div className="mr-4 mt-0.5 space-y-0.5 border-r border-metal/40 pr-1">
          {items.map(child => {
            const pi = PLATFORM_ICONS[child.platform];
            return (
              <NavLink
                key={child.to}
                to={child.to}
                data-testid={`nav-${child.to.slice(1)}`}
                className={({ isActive }) => `flex items-center gap-2 px-3 py-1.5 text-[12px] rounded transition-colors ${isActive ? "bg-[rgba(197,160,89,0.12)] text-gold" : "text-zinc-500 hover:text-white hover:bg-white/5"}`}
              >
                {pi && (
                  <span className={`w-4 h-4 rounded flex items-center justify-center ${pi.bg} ${pi.color} shrink-0`}>
                    {pi.svg}
                  </span>
                )}
                <span className="truncate">{child.label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function Shell({ children, hostPanel = null }) {
  const { session, updateSession } = useSession();
  const { user, isOwner, hasPerm } = useAuth();
  const streamMode = session?.stream_mode;
  const dateStr = new Date().toLocaleDateString("ar-EG", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const timeStr = "11:11 PM"; // permanent identity time
  const year = new Date().getFullYear();

  // Filter NAV by current user's permissions. OWNER sees all except case-
  // specific tabs which are hidden globally (they belong INSIDE each case).
  const streamerAllowed = isOwner || user?.role === "OWNER" || user?.account_type === "OWNER" || (user?.membership_plan || "").toUpperCase() === "STREAMER";
  // Case-specific tabs (suspects/evidence/forensics/timeline) live INSIDE
  // each case detail page — NOT as global sidebar entries. Hidden for ALL
  // roles including OWNER; OWNER manages these from within each case.
  const visibleNav = NAV.filter((n) =>
    !n.caseTab  // 4 case-specific tabs live INSIDE each case, never global
    && (!n.ownerOnly || isOwner)
    && (!n.streamerOrOwner || streamerAllowed)
    && (!n.perm || isOwner || hasPerm(n.perm))
    && (!n.streamerHidden || streamerAllowed)
  );

  // ── P3: Custom sidebar items (OWNER-configured, data-driven).  The
  // base NAV above always remains authoritative — custom items are ADDED
  // between the base entries and "Owner Center" so they can't shadow
  // critical navigation. Server enforces visibility; we render only what
  // was returned.
  const [customItems, setCustomItems] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/nav/items");
        if (alive) setCustomItems(Array.isArray(data?.items) ? data.items : []);
      } catch { if (alive) setCustomItems([]); }
    })();
    return () => { alive = false; };
  }, [user?.user_id, user?.membership_plan]);

  // Auto-play case-specific ambience
  const activeCase = session?.current_case_id;
  useEffect(() => {
    if (activeCase) playAmbienceFor(activeCase);
    return () => {};
  }, [activeCase]);
  useEffect(() => () => stopAmbienceBed(), []);
  // Global OBS preset hotkeys — REMOVED (production correction pass):
  // Application-wide keyboard shortcuts were causing accidental gameplay
  // triggers when users typed numbers in form fields. No shortcut system
  // is active in the application anymore.
  return (
    <div className="min-h-screen flex" dir="rtl" data-testid="shell">
      {/* RIGHT sidebar (RTL) with OK FBI emblem */}
      <aside className="w-56 lg:w-64 shrink-0 border-l border-metal bg-gradient-to-b from-[#0F0F12] to-[#08080A] fixed right-0 top-0 h-screen z-30 flex flex-col" data-testid="sidebar">
        <div className="pt-6 pb-5 px-3 border-b border-metal flex flex-col items-center">
          <img
            src={assetUrl("/generated_images/okfbi-emblem-4k-transparent.png")}
            alt="OK FBI"
            className="w-[92%] max-w-[220px] object-contain transition-transform duration-300 hover:scale-[1.02] cursor-default select-none"
            style={{
              filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.75)) drop-shadow(0 2px 6px rgba(197,160,89,0.18))",
              background: "transparent"
            }}
            draggable={false}
            data-testid="sidebar-emblem"
            onError={(e) => {
              if (!e.target.dataset.triedFallback) {
                e.target.dataset.triedFallback = "1";
                e.target.src = "/generated_images/okfbi-emblem-4k-transparent.png";
              }
            }}
          />
        </div>
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {visibleNav.map((item) => {
            const { to, label, icon: Icon, isGroup, children: kids } = item;

            /* ── Expandable group (e.g. "منصات البث") ── */
            if (isGroup && kids) {
              return (
                <NavGroup key={to} to={to} label={label} Icon={Icon} items={kids} />
              );
            }

            /* ── Normal nav item ── */
            return (
              <NavLink key={to} to={to} end
                data-testid={`nav-${to === "/" ? "home" : to.slice(1)}`}
                className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 text-[13px] rounded transition-colors ${isActive ? "bg-[rgba(197,160,89,0.12)] text-gold border-r-2 border-gold" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
                <Icon size={16} strokeWidth={1.7} className="shrink-0" />
                <span className="flex-1 truncate">{label}</span>
              </NavLink>
            );
          })}
          {customItems.length > 0 && (
            <div className="pt-2 mt-2 border-t border-metal/50" data-testid="nav-custom-section">
              {customItems.map((it) => {
                const Icon = CUSTOM_ICONS[it.icon] || BookOpen;
                const to = it.external_url ? it.external_url
                          : it.page_id ? `/p/${it.page_id}` : "#";
                const linkProps = it.external_url
                  ? { href: to, target: "_blank", rel: "noreferrer" }
                  : { to };
                const Comp = it.external_url ? "a" : NavLink;
                return (
                  <Comp key={it.id} {...linkProps}
                    data-testid={`nav-custom-${it.id}`}
                    className={({ isActive } = {}) => `flex items-center gap-3 px-3 py-2 text-[13px] rounded transition-colors ${isActive ? "bg-[rgba(197,160,89,0.12)] text-gold border-r-2 border-gold" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
                    <Icon size={16} strokeWidth={1.7} />
                    <span className="flex-1">{it.label}</span>
                    {it.pin_required && <Lock size={11} className="text-yellow-500/70"/>}
                  </Comp>
                );
              })}
            </div>
          )}
        </nav>
        <div className="p-3 border-t border-metal space-y-3">
          {streamerAllowed && (
          <button onClick={() => updateSession({ stream_mode: !streamMode })} data-testid="stream-mode-toggle"
            className={`w-full flex items-center justify-center gap-2 py-2 rounded text-xs transition-colors ${streamMode ? "bg-red-900/40 text-red-300 border border-red-800/50" : "bg-zinc-900/60 text-zinc-400 border border-zinc-800 hover:border-gold"}`}>
            {streamMode ? <><Radio size={14} className="animate-pulse" /> وضع البث · مباشر</> : <><Radio size={14} /> وضع البث</>}
          </button>
          )}
          {/* OK ORIGINAL — Premium Ownership Signature Plate */}
          <div className="pt-3 pb-1" data-testid="sidebar-credit">
            {/* Metallic divider */}
            <div className="relative h-px mb-3" aria-hidden>
              <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, transparent 0%, rgba(197,160,89,0.55) 50%, transparent 100%)" }} />
              <div className="absolute inset-0 blur-[2px] opacity-40" style={{ background: "linear-gradient(90deg, transparent 0%, rgba(197,160,89,0.55) 50%, transparent 100%)" }} />
            </div>
            {/* Tiny embossed OK crest */}
            <div className="flex justify-center mb-2" aria-hidden>
              <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"
                   style={{ filter: "drop-shadow(0 1px 0 rgba(0,0,0,0.9)) drop-shadow(0 0 4px rgba(197,160,89,0.35))" }}>
                <defs>
                  <linearGradient id="okCrestFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#E8D08A" />
                    <stop offset="55%" stopColor="#C5A059" />
                    <stop offset="100%" stopColor="#7a5a1c" />
                  </linearGradient>
                </defs>
                <path d="M12 1.6 L21.5 6.2 V13 C21.5 18 17.2 21.4 12 22.4 C6.8 21.4 2.5 18 2.5 13 V6.2 Z"
                      fill="none" stroke="url(#okCrestFill)" strokeWidth="1.2" />
                <text x="12" y="15" textAnchor="middle" fontSize="8" fontWeight="800"
                      fill="url(#okCrestFill)" fontFamily="ui-monospace, monospace" letterSpacing="1">OK</text>
              </svg>
            </div>
            {/* Signature typography */}
            <div className="text-center leading-tight">
              <div className="text-[13px] tracking-[0.45em] font-cairo"
                   style={{
                     color: "#C5A059",
                     textShadow: "0 1px 0 rgba(0,0,0,0.85), 0 0 6px rgba(197,160,89,0.25)",
                     fontWeight: 600,
                   }}>
                OK ORIGINAL
              </div>
              <div className="mt-1 text-[9px] tracking-[0.5em] font-mono-ar text-zinc-400/85">
                INVESTIGATION GAME
              </div>
              <div className="mt-3 text-[10px] tracking-[0.3em] font-mono-ar text-zinc-300/90" style={{ fontWeight: 500 }}>
                DESIGNED &amp; DEVELOPED BY OK
              </div>
              <div className="mt-2 text-[8px] tracking-[0.22em] font-mono-ar text-zinc-600/90">
                © {year} OK ORIGINAL · ALL RIGHTS RESERVED
              </div>
              {/* Engraved serial mark */}
              <div className="mt-2.5 inline-flex items-center gap-1.5 text-[8px] font-mono-ar text-zinc-600/85 tracking-[0.3em]">
                <span className="w-1 h-1 rounded-full bg-gold/60" aria-hidden />
                <span>
                  CREATED BY //{" "}
                  <span style={{ color: "#C5A059", textShadow: "0 0 4px rgba(197,160,89,0.25)" }}>AMW</span>
                </span>
                <span className="w-1 h-1 rounded-full bg-gold/60" aria-hidden />
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 mr-56 lg:mr-64 min-h-screen relative">
        <TopBar dateStr={dateStr} timeStr={timeStr} streamMode={streamMode} />
        <main className="p-6 lg:p-8 relative z-10">{children}</main>
        <SiteFooter year={year} />
        {streamMode && <StreamWatermark />}
        {/* Floating HOST CONTROLS panel — REMOVED in production correction pass.
            Case difficulty and stage controls belong inside the case editor
            (OWNER) or streamer dashboard, not as a permanent screen overlay. */}
      </div>
      {/* Presenter cue cards — only for STREAMER/OWNER, and only on host/streamer routes */}
      {(isOwner || (user?.membership_plan || "").toUpperCase() === "STREAMER") &&
       (typeof window !== "undefined" && window.location.pathname.startsWith("/host/")) &&
        <CueCardsDock />}
    </div>
  );
}

function SiteFooter({ year }) {
  // P9: single, non-duplicating footer.  All brand credits (OK ORIGINAL /
  // CREATED BY // AMW / © YEAR) already live in the sidebar signature plate
  // above — repeating them here would be visible duplication on every page.
  // This footer only carries legal links + the mandatory fiction disclaimer.
  return (
    <footer className="mt-8 border-t border-metal/60 py-4 px-6 relative z-10" data-testid="site-footer">
      <div className="max-w-[1080px] mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <div className="text-[10px] leading-relaxed text-zinc-500 font-cairo max-w-[720px]" data-testid="shell-fiction-disclaimer">
          OK FBI لعبة تحقيق تفاعلية خيالية لأغراض الترفيه. جميع القضايا والشخصيات والأحداث من وحي الخيال، وأي تشابه مع أشخاص أو أحداث حقيقية هو محض مصادفة. OK FBI ليست جهة حكومية أو أمنية ولا ترتبط بمكتب التحقيقات الفيدرالي (FBI) أو أي جهة رسمية.
        </div>
        <div className="flex items-center gap-3 shrink-0 text-[11px] font-cairo tracking-[0.25em]" data-testid="shell-legal-links">
          <Link to="/terms" className="text-zinc-400 hover:text-gold transition-colors" data-testid="shell-terms-link">شروط الاستخدام</Link>
          <span className="text-zinc-700">·</span>
          <Link to="/privacy" className="text-zinc-400 hover:text-gold transition-colors" data-testid="shell-privacy-link">سياسة الخصوصية</Link>
          <span className="text-zinc-700">·</span>
          <span className="text-zinc-500 tracking-widest">© {year}</span>
        </div>
      </div>
    </footer>
  );
}

function StreamWatermark() {
  return (
    <div className="fixed bottom-4 right-4 z-40 pointer-events-none opacity-60 text-right" data-testid="stream-watermark">
      <div className="text-gold text-[10px] tracking-[0.3em] font-cairo font-bold">OK FBI · BY OK</div>
      <div className="text-zinc-500 text-[8px] tracking-[0.25em] font-cairo">OK ORIGINAL</div>
    </div>
  );
}

function TopBar({ dateStr, timeStr, streamMode }) {
  const { session } = useSession();
  const { user, isOwner } = useAuth();
  const [sound, setSound] = useState(isSoundEnabled());
  const [amb, setAmb] = useState(isAmbienceEnabled());
  const [ambVol, setAmbVol] = useState(getAmbienceVolume());
  const [ambOpen, setAmbOpen] = useState(false);
  const case_id = session?.current_case_id;
  const toggleSound = () => { const n = !sound; setSoundEnabled(n); setSound(n); if (n) sfx.click(); };
  const toggleAmb = () => { const n = !amb; setAmbienceEnabled(n); setAmb(n); if (n && case_id) playAmbienceFor(case_id); sfx.click(); };
  const changeVol = (v) => { setAmbVol(v); setAmbienceVolume(v); };
  // === Rank / Level derivation ===
  // OWNER is DECOUPLED from the normal Level/XP/Rank progression loop.
  // Non-OWNER users get level derived from their stored `points` (see backend
  // GAME_RANKS thresholds). Owner shows a supreme "القيادة" plate instead.
  const points = Number(user?.points || 0);
  // Level = 1 + floor(points / 1000) capped at 99 — purely for display; server
  // remains the source of truth for the `rank` object attached to leaderboard rows.
  const levelNum = Math.max(1, Math.min(99, 1 + Math.floor(points / 1000)));
  return (
    <header className="h-16 border-b border-metal bg-[#0B0B0E]/95 backdrop-blur-xl sticky top-0 z-20 flex items-center px-6 lg:px-8 gap-6" data-testid="topbar">
      <div className="flex items-center gap-2 text-[10px] font-mono-ar text-zinc-500" data-testid="db-status">
        <span className="text-emerald-400 animate-pulse">●</span>
        <span>متصل بالقاعدة</span>
      </div>
      <div className="hidden md:flex items-center gap-3 text-xs">
        <span className="text-zinc-500">{isOwner ? "المالك:" : "المُقدِّم:"}</span>
        {user ? (
          <Link
            to="/profile"
            className="text-gold font-cairo hover:text-yellow-300 hover:underline transition-colors cursor-pointer"
            title="الملف الشخصي للمحقق"
            data-testid="topbar-user-name"
          >
            {`${user.name || "?"} · #${user.agent_id || "?"}`}
          </Link>
        ) : (
          <span className="text-gold font-cairo" data-testid="topbar-user-name">—</span>
        )}
        {user?.tiktok_open_id && <TikTokVerifiedBadge />}
        {user && !isOwner && <MembershipBadge plan={user.membership_plan} />}
        <span className="text-zinc-700">·</span>
        <span className="text-zinc-400 text-[10px] font-cairo" data-testid="topbar-user-role">
          {user?.role === "OWNER" ? "المالك" :
           user?.role === "ADMIN" ? "مدير" :
           user?.role === "CASE_EDITOR" ? "كاتب قضايا" :
           user?.role === "INVESTIGATOR" ? "محقق" :
           user?.role === "VIEWER" ? "مشاهد" : ""}
        </span>
      </div>
      {isOwner ? (
        <div className="flex items-center gap-2">
          <OwnerCommandBadge />
          <OwnerEvidenceBell />
        </div>
      ) : (
        <div className="flex items-center gap-2 border border-gold/40 px-2.5 py-1 text-[11px] font-mono-ar bg-[rgba(197,160,89,0.05)]" data-testid="level-badge">
          <span className="text-zinc-500">المستوى</span>
          <span className="text-gold font-black text-sm">{levelNum}</span>
        </div>
      )}
      {/* Persistent TikTok LIVE indicator — visible only on host pages (OWNER + STREAMER) */}
      {(isOwner || (user?.membership_plan || "").toUpperCase() === "STREAMER") && (
        <TikTokLiveBadge caseId={case_id} />
      )}
      {/* Host logout */}
      <HostLogoutButton compact={true} />
      {case_id && (
        <>
          <div className="flex items-center gap-3 border border-metal px-3 py-1 text-[11px] font-mono-ar">
            <span className="text-zinc-500">القضية</span>
            <span className="text-white">{case_id.toUpperCase()}</span>
            <span className="text-gold">•</span>
            <span className="text-cyan-400">{
              ({"NOT_STARTED":"لم يبدأ","OPEN":"مفتوح","CLOSED":"مغلق","LOCKED":"مقفل"})
                [(session?.voting_status || "NOT_STARTED")] ||
                (session?.voting_status || "NOT_STARTED").replace("_", " ")
            }</span>
          </div>
          <CaseTimer caseId={case_id} />
        </>
      )}
      <div className="flex-1" />
      {streamMode && (
        <div className="flex items-center gap-2 text-[10px] text-red-400 font-mono-ar bg-red-950/40 border border-red-900/60 px-3 py-1">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> جارٍ التسجيل للبث
        </div>
      )}
      <button onClick={toggleSound} data-testid="sound-toggle" aria-pressed={sound} data-state={sound ? "on" : "off"}
        title={sound ? "الصوت مفعل" : "الصوت مكتوم"} aria-label={sound ? "كتم الصوت" : "تفعيل الصوت"}
        className={`w-9 h-9 border rounded-sm flex items-center justify-center transition-colors ${sound ? "border-gold/40 text-gold hover:bg-[rgba(197,160,89,0.1)]" : "border-zinc-800 text-zinc-600 hover:text-zinc-400"}`}>
        {sound ? <Volume2 size={15} /> : <VolumeX size={15} />}
      </button>
      {/* Ambience toggle */}
      <div className="relative">
        <button onClick={() => setAmbOpen(o => !o)} onDoubleClick={toggleAmb} data-testid="ambience-toggle"
          title="أجواء القضية"
          className={`w-9 h-9 border rounded-sm flex items-center justify-center transition-colors ${amb ? "border-gold/40 text-gold hover:bg-[rgba(197,160,89,0.1)]" : "border-zinc-800 text-zinc-600 hover:text-zinc-400"}`}>
          <Music2 size={15} />
        </button>
        {ambOpen && (
          <div className="absolute top-11 left-0 w-56 panel border-metal p-3 z-40 text-xs" onMouseLeave={() => setAmbOpen(false)} data-testid="ambience-panel">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gold font-cairo">أجواء القضية</span>
              <button onClick={toggleAmb} data-testid="ambience-onoff" className={`text-[10px] px-2 py-0.5 border ${amb ? "border-emerald-700 text-emerald-400" : "border-zinc-700 text-zinc-500"}`}>{amb ? "ON" : "OFF"}</button>
            </div>
            <div className="text-[10px] text-zinc-500 mb-1">الحجم {Math.round(ambVol * 100)}%</div>
            <input type="range" min="0" max="1" step="0.01" value={ambVol} onChange={(e) => changeVol(parseFloat(e.target.value))}
              data-testid="ambience-volume" className="w-full accent-yellow-600" />
            <div className="text-[9px] text-zinc-600 mt-2 leading-tight">جوّ صوتي خفيف يتغير حسب القضية النشطة. اضغط مرتين لتفعيل/إيقاف سريع.</div>
          </div>
        )}
      </div>
      <div className="text-right hidden lg:block">
        <div className="text-[10px] text-zinc-500 tracking-widest">{dateStr}</div>
        <div className="text-lg font-cairo text-gold tracking-widest" style={{ fontVariantNumeric: "tabular-nums" }} data-testid="signature-time">{timeStr}</div>
      </div>
    </header>
  );
}

function HostPanelDock({ children }) {
  const [open, setOpen] = useState(true);
  return (
    <>
      {open ? (
        <aside className="fixed bottom-4 left-4 w-[380px] max-h-[70vh] overflow-hidden panel gold-border shadow-2xl z-40 flex flex-col" data-testid="host-panel">
          <div className="flex items-center justify-between px-4 py-2 border-b border-metal bg-black/50">
            <div className="flex items-center gap-2 text-gold text-xs font-mono-ar"><Zap size={12} /> HOST CONTROLS</div>
            <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-gold" data-testid="host-panel-close"><ChevronLeft size={16} /></button>
          </div>
          <div className="overflow-y-auto p-4">{children}</div>
        </aside>
      ) : (
        <button onClick={() => setOpen(true)} data-testid="host-panel-open"
          className="fixed bottom-4 left-4 bg-black border border-gold text-gold px-3 py-2 z-40 flex items-center gap-2 text-xs font-mono-ar hover:bg-[rgba(197,160,89,0.1)]">
          <Zap size={14} /> HOST <ChevronRight size={14} />
        </button>
      )}
    </>
  );
}


// ================ Neutral OK FBI TikTok-Verified Badge ================
// Displayed next to a user name in the TopBar when their account was created
// (or linked) via an OFFICIAL TikTok OAuth flow. Intentionally NOT the TikTok
// verification checkmark — this is an OK FBI internal marker only.
function TikTokVerifiedBadge() {
  return (
    <span
      data-testid="tiktok-verified-badge"
      title="مرتبط بحساب TikTok"
      className="inline-flex items-center gap-1 border border-emerald-800/70 text-emerald-300 text-[9px] font-cairo tracking-wider px-1.5 py-0.5 bg-emerald-950/25"
    >
      <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M19.6 6.9a5.4 5.4 0 0 1-3.5-2.1V15a5.5 5.5 0 1 1-5.5-5.5c.3 0 .6 0 .9.1v2.6a2.9 2.9 0 1 0 2 2.8V2h2.7a5.4 5.4 0 0 0 3.4 4.9z"/>
      </svg>
      <span>موثّق TikTok</span>
    </span>
  );
}


function OwnerEvidenceBell() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let alive = true;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/owner/evidence-notifications/unread-count");
        if (alive) setCount(data?.unread_count || 0);
      } catch {
        if (alive) setCount(0);
      }
    };
    fetchCount();
    const iv = setInterval(fetchCount, 20000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, []);

  return (
    <Link
      to="/host/evidence-notifications"
      className="relative p-2 text-zinc-400 hover:text-yellow-300 transition-colors flex items-center justify-center border border-metal/70 rounded hover:border-yellow-700/60 bg-black/40"
      title={count > 0 ? `${count} إشعار أدلة سرية غير مقروء` : "رسائل الأدلة السرية"}
      data-testid="owner-evidence-bell"
    >
      <Bell size={15} />
      {count > 0 && (
        <span
          className="absolute -top-1.5 -right-1.5 min-w-[17px] h-[17px] px-1 bg-red-600 border border-red-400 text-white text-[9px] font-mono font-bold rounded-full flex items-center justify-center shadow-lg animate-pulse"
          data-testid="evidence-unread-badge"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}


// ================ OWNER Command Badge — supreme identity plate ================
// Shown ONLY for the OWNER account in the TopBar. Replaces the normal
// LEVEL/XP progression display. Uses the same gold theme as the sidebar credit
// to signal supreme platform authority. Fully decoupled from `points`.
function OwnerCommandBadge() {
  return (
    <div
      data-testid="owner-command-badge"
      className="flex items-center gap-2 px-3 py-1.5 font-cairo select-none"
      style={{
        border: "1px solid rgba(232,208,138,0.55)",
        background: "linear-gradient(180deg, rgba(197,160,89,0.14) 0%, rgba(122,90,28,0.10) 100%)",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.6) inset, 0 6px 22px rgba(197,160,89,0.18)",
        borderRadius: 2,
      }}
      title="حساب القيادة"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
        <defs>
          <linearGradient id="ownerBadgeFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E8D08A" />
            <stop offset="55%" stopColor="#C5A059" />
            <stop offset="100%" stopColor="#7a5a1c" />
          </linearGradient>
        </defs>
        <path
          d="M12 1.6 L21.5 6.2 V13 C21.5 18 17.2 21.4 12 22.4 C6.8 21.4 2.5 18 2.5 13 V6.2 Z"
          fill="none"
          stroke="url(#ownerBadgeFill)"
          strokeWidth="1.4"
        />
        <text x="12" y="15" textAnchor="middle" fontSize="7" fontWeight="800"
              fill="url(#ownerBadgeFill)" fontFamily="ui-monospace, monospace" letterSpacing="1">
          OK
        </text>
      </svg>
      <span
        className="text-[12px] tracking-[0.45em]"
        style={{
          color: "#E8D08A",
          textShadow: "0 1px 0 rgba(0,0,0,0.85), 0 0 8px rgba(197,160,89,0.35)",
          fontWeight: 700,
        }}
      >
        القيادة
      </span>
    </div>
  );
}


// ================ Persistent Stream Platforms Widget (Header) ================
// Sits in the header on every page for STREAMER and OWNER.
// Displays the unified streaming status and allows clicking to open the StreamPlatformsModal
// to connect/disconnect TikTok, Twitch, and Kick independently without manual username typing.
export function StreamPlatformsHeaderWidget({ caseId }) {
  const { user, isOwner } = useAuth();
  const isStreamerOrOwner = isOwner
    || user?.role === "OWNER"
    || user?.account_type === "OWNER"
    || user?.role === "STREAMER"
    || user?.account_type === "STREAMER"
    || (user?.membership_plan || "").toUpperCase() === "STREAMER";

  const [conn, setConn] = useState(null);
  const [twitchConn, setTwitchConn] = useState(null);
  const [kickConn, setKickConn] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    if (!isStreamerOrOwner) return null;
    let tt = null;
    let tw = null;
    let kc = null;
    try {
      const { data } = await api.get("/tiktok/status");
      tt = data;
      setConn(data);
    } catch { /* silent */ }
    try {
      const { data: twRes } = await api.get("/twitch/status");
      tw = twRes?.connection || null;
      setTwitchConn(tw);
    } catch { /* silent */ }
    try {
      const { data: kcRes } = await api.get("/kick/status");
      kc = kcRes?.connection || null;
      setKickConn(kc);
    } catch { /* silent */ }
    return { conn: tt, twitchConn: tw, kickConn: kc };
  }, [isStreamerOrOwner]);

  useEffect(() => {
    if (!isStreamerOrOwner) return;
    let stop = false;
    let iv = null;
    let currentMs = 0;

    const tick = async () => {
      const fresh = await load();
      if (stop) return;
      const fast = !!(
        (fresh?.conn && (fresh.conn.connected || fresh.conn.real_live)) ||
        fresh?.twitchConn?.connected ||
        fresh?.kickConn?.connected
      );
      const nextMs = fast ? 3500 : 25000;
      if (nextMs !== currentMs) {
        currentMs = nextMs;
        if (iv) clearInterval(iv);
        iv = setInterval(tick, nextMs);
      }
    };
    tick();
    return () => { stop = true; if (iv) clearInterval(iv); };
  }, [isStreamerOrOwner, load]);

  // Handle OAuth return queries in the URL across all pages
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const stream = params.get("stream");
    const streamError = params.get("stream_error");

    if (stream) {
      if (stream === "tiktok_connected") toast.success("🎉 تم ربط حساب TikTok بنجاح!");
      else if (stream === "twitch_connected") toast.success("🎉 تم ربط حساب Twitch بنجاح!");
      else if (stream === "kick_connected") toast.success("🎉 تم ربط حساب Kick بنجاح!");
      load();
      // Clean query parameter from URL without page reload
      params.delete("stream");
      const newQuery = params.toString() ? `?${params.toString()}` : "";
      window.history.replaceState({}, document.title, `${window.location.pathname}${newQuery}${window.location.hash}`);
    } else if (streamError) {
      toast.error(`فشل ربط المنصة: ${streamError}`);
      params.delete("stream_error");
      const newQuery = params.toString() ? `?${params.toString()}` : "";
      window.history.replaceState({}, document.title, `${window.location.pathname}${newQuery}${window.location.hash}`);
    }
  }, [load]);

  if (!isStreamerOrOwner) return null;

  const isLive = !!conn?.real_live || twitchConn?.live_status === "online" || kickConn?.live_status === "online";
  const isTikTokConnected = !!conn?.connected;
  const isTwitchConnected = !!twitchConn?.connected;
  const isKickConnected = !!kickConn?.connected;
  const anyConnected = isTikTokConnected || isTwitchConnected || isKickConnected;
  const connCount = [isTikTokConnected, isTwitchConnected, isKickConnected].filter(Boolean).length;

  const tone = isLive
    ? "text-emerald-300 border-emerald-700 bg-emerald-950/35 hover:bg-emerald-950/50 shadow-[0_0_12px_rgba(16,185,129,0.2)]"
    : anyConnected
    ? "text-gold border-gold/60 bg-[rgba(197,160,89,0.10)] hover:bg-[rgba(197,160,89,0.22)] shadow-[0_0_10px_rgba(197,160,89,0.15)]"
    : "text-neutral-300 border-neutral-700 bg-neutral-900/80 hover:bg-neutral-800 hover:border-gold/50";

  const label = isLive
    ? `بث مباشر · ${connCount}/3`
    : anyConnected
    ? `منصات البث · ${connCount}/3 متصل`
    : "ربط منصة البث";

  const title = `منصات البث: TikTok (${isTikTokConnected ? "متصل" : "غير متصل"}) · Twitch (${isTwitchConnected ? "متصل" : "غير متصل"}) · Kick (${isKickConnected ? "متصل" : "غير متصل"}`;

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        data-testid="topbar-stream-platforms-link"
        title={title}
        className={`flex items-center gap-2 border px-3 py-1.5 text-[11px] font-cairo transition-all rounded-sm cursor-pointer ${tone}`}
      >
        <span
          className={`w-2 h-2 rounded-full ${
            isLive
              ? "bg-emerald-400 animate-pulse"
              : anyConnected
              ? "bg-gold animate-pulse"
              : "bg-red-500"
          }`}
        />
        <Video size={13} className="shrink-0" />
        <span className="font-bold">{label}</span>
      </button>

      <StreamPlatformsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onRefresh={load}
      />
    </>
  );
}

// Backward-compatible alias
export function TikTokLiveBadge({ caseId }) {
  return <StreamPlatformsHeaderWidget caseId={caseId} />;
}
